// Collaborators API Service - Enterprise Grade Integration
// Implements all 48+ endpoints from the enterprise backend
// UPDATED: Full compatibility with FRONTEND-PROFILE-GUIDE.md
const API_BASE = `${
  process.env.REACT_APP_API_URL || "http://localhost:8000/api"
}/collaborators`;
const PROFILE_API_BASE = `${
  process.env.REACT_APP_API_URL || "http://localhost:8000/api"
}/profile`;
// Helper function to get auth headers
const getAuthHeaders = () => {
  try {
    //
    // Try to get token directly from localStorage (primary method)
    let token = localStorage.getItem("token");
    // Fallback: try authToken key
    if (!token) {
      token = localStorage.getItem("authToken");
    }
    // Fallback: try user object (legacy method)
    if (!token) {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (user && user.token) {
        token = user.token;
      }
    }
    if (token) {
      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
    }
    return {
      "Content-Type": "application/json",
    };
  } catch (error) {
    return {
      "Content-Type": "application/json",
    };
  }
};
// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `HTTP error! status: ${response.status}`
    );
  }
  return response.json();
};
// UNIFIED PROFILE DASHBOARD API (New - per FRONTEND-PROFILE-GUIDE.md Section 2)
export const dashboardAPI = {
  // GET /api/profile/dashboard - Get own profile dashboard (single call for all data)
  getOwnDashboard: async () => {
    try {
      const response = await fetch(`${PROFILE_API_BASE}/dashboard`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      return data.success ? data.data : null;
    } catch (error) {
      throw error;
    }
  },
  // GET /api/profile/dashboard/:userId - Get another user's profile dashboard
  getUserDashboard: async (userId) => {
    try {
      const response = await fetch(`${PROFILE_API_BASE}/dashboard/${userId}`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      return data.success ? data.data : null;
    } catch (error) {
      throw error;
    }
  },
  // POST /api/profile/dashboard/invalidate - Invalidate profile cache
  invalidateCache: async (userId = null) => {
    try {
      const response = await fetch(`${PROFILE_API_BASE}/dashboard/invalidate`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(userId ? { userId } : {}),
      });
      const data = await handleResponse(response);
      return data;
    } catch (error) {
      // Don't throw - cache invalidation failure shouldn't block other operations
      return { success: false, error: error.message };
    }
  },
  // GET /api/profile/dashboard/stats - Get cache stats (admin/debug)
  getCacheStats: async () => {
    try {
      const response = await fetch(`${PROFILE_API_BASE}/dashboard/stats`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      return data;
    } catch (error) {
      throw error;
    }
  },
};
// 1. PROFILE MANAGEMENT (3 endpoints)
export const profileAPI = {
  // GET /api/collaborators/profile - Get own profile
  getMyProfile: async () => {
    try {
      const response = await fetch(`${API_BASE}/profile`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      return data.success ? data.data.profile : null;
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/profile/:userId - Get user's public profile
  getUserProfile: async (userId) => {
    try {
      const response = await fetch(`${API_BASE}/profile/${userId}`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      if (data.success) {
        return {
          profile: data.data.profile,
          relationship: data.data.relationship,
          collaborationStats: data.data.collaborationStats,
          recentPosts: data.data.recentPosts,
          recentRatings: data.data.recentRatings,
        };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },
  // PUT /api/collaborators/profile - Update own profile
  updateProfile: async (profileData) => {
    try {
      const response = await fetch(`${API_BASE}/profile`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: profileData.name,
          bio: profileData.bio,
          education: profileData.education,
          researchInterests: profileData.researchInterests,
          field: profileData.field,
          interests: profileData.interests,
          professionalRole: profileData.professionalRole,
          goals: profileData.goals,
          isOpenForCollaboration: profileData.isOpenForCollaboration,
          externalLinks: profileData.externalLinks,
          profilePrivacy: profileData.profilePrivacy,
        }),
      });
      const data = await handleResponse(response);
      return data.success ? data.data.profile : null;
    } catch (error) {
      throw error;
    }
  },
};
// 2. FOLLOWING SYSTEM (6 endpoints - updated per FRONTEND-PROFILE-GUIDE.md)
export const followingAPI = {
  // POST /api/collaborators/follow/:userId - Follow user
  // Rate Limited: 10 actions per minute
  followUser: async (userId, source = "profile") => {
    try {
      const response = await fetch(`${API_BASE}/follow/${userId}`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ source }),
      });
      const data = await handleResponse(response);
      // Invalidate cache after follow
      dashboardAPI.invalidateCache();
      return data;
    } catch (error) {
      throw error;
    }
  },
  // DELETE /api/collaborators/follow/:userId - Unfollow user
  unfollowUser: async (userId) => {
    try {
      const response = await fetch(`${API_BASE}/follow/${userId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      // Invalidate cache after unfollow
      dashboardAPI.invalidateCache();
      return data;
    } catch (error) {
      throw error;
    }
  },
  // DELETE /api/collaborators/followers/:userId - Remove a follower
  // This removes someone who is following YOU
  removeFollower: async (userId) => {
    try {
      const response = await fetch(`${API_BASE}/followers/${userId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      // Invalidate cache after removing follower
      dashboardAPI.invalidateCache();
      return data;
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/followers/:userId - Get user's followers
  // Supports both page-based and cursor-based pagination
  getFollowers: async (userId, page = 1, limit = 20, cursor = null) => {
    try {
      const params = new URLSearchParams();
      if (cursor) {
        params.append("cursor", cursor);
      } else {
        params.append("page", page.toString());
      }
      params.append("limit", limit.toString());
      const response = await fetch(
        `${API_BASE}/followers/${userId}?${params}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      if (data.success) {
        return {
          followers: data.data.followers,
          pagination: data.data.pagination,
        };
      }
      return { followers: [], pagination: {} };
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/following/:userId - Get who user follows
  // Supports both page-based and cursor-based pagination
  getFollowing: async (userId, page = 1, limit = 20, cursor = null) => {
    try {
      const params = new URLSearchParams();
      if (cursor) {
        params.append("cursor", cursor);
      } else {
        params.append("page", page.toString());
      }
      params.append("limit", limit.toString());
      const response = await fetch(
        `${API_BASE}/following/${userId}?${params}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      if (data.success) {
        return {
          following: data.data.following,
          pagination: data.data.pagination,
        };
      }
      return { following: [], pagination: {} };
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/suggestions - Get follow suggestions
  getFollowSuggestions: async (limit = 10) => {
    try {
      const response = await fetch(`${API_BASE}/suggestions?limit=${limit}`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      return data.success ? data.data.suggestions : [];
    } catch (error) {
      throw error;
    }
  },
};
// 3. COLLABORATION REQUESTS (5 endpoints)
export const collaborationAPI = {
  // GET /api/collaborators/collaborators/:userId - Get user's collaborators list
  getCollaboratorsList: async (
    userId,
    page = 1,
    limit = 20,
    sortBy = "recent"
  ) => {
    try {
      const response = await fetch(
        `${API_BASE}/collaborators/${userId}?page=${page}&limit=${limit}&sortBy=${sortBy}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      if (data.success) {
        return {
          collaborators: data.data?.collaborators || [],
          pagination: data.data?.pagination || {
            totalCount: 0,
            currentPage: 1,
            totalPages: 0,
          },
          meta: data.meta || {},
        };
      }
      return {
        collaborators: [],
        pagination: { totalCount: 0, currentPage: 1, totalPages: 0 },
        meta: {},
      };
    } catch (error) {
      // Return empty data instead of throwing to prevent UI crashes
      return {
        collaborators: [],
        pagination: { totalCount: 0, currentPage: 1, totalPages: 0 },
        meta: {},
      };
    }
  },
  // GET /api/collaborators/collaborators/:userId - Cursor-based pagination
  getCollaboratorsWithCursor: async (
    userId,
    cursor = null,
    limit = 20,
    sortBy = "recent"
  ) => {
    try {
      const params = new URLSearchParams();
      if (cursor) params.append("cursor", cursor);
      params.append("limit", limit.toString());
      params.append("sortBy", sortBy);
      const response = await fetch(
        `${API_BASE}/collaborators/${userId}?${params}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      if (data.success) {
        return {
          collaborators: data.data?.collaborators || [],
          pagination: data.data?.pagination || {
            hasNext: false,
            hasPrev: false,
            nextCursor: null,
            prevCursor: null,
            type: "cursor",
            count: 0,
          },
          meta: data.meta || {},
        };
      }
      return {
        collaborators: [],
        pagination: {
          hasNext: false,
          hasPrev: false,
          nextCursor: null,
          prevCursor: null,
          type: "cursor",
          count: 0,
        },
        meta: {},
      };
    } catch (error) {
      return {
        collaborators: [],
        pagination: {
          hasNext: false,
          hasPrev: false,
          nextCursor: null,
          prevCursor: null,
          type: "cursor",
          count: 0,
        },
        meta: {},
      };
    }
  },
  // POST /api/collaborators/collaborate/:userId - Send collaboration request
  sendCollaborationRequest: async (userId, requestData) => {
    try {
      const response = await fetch(`${API_BASE}/collaborate/${userId}`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          researchRoomId: requestData.researchRoomId,
          message: requestData.message,
          collaborationType: requestData.collaborationType || "research",
          expectedDuration: requestData.expectedDuration || "not_specified",
          skillsNeeded: requestData.skillsNeeded || [],
        }),
      });
      return await handleResponse(response);
    } catch (error) {
      throw error;
    }
  },
  // PUT /api/collaborators/collaborate/:requestId/respond - Respond to request
  respondToCollaborationRequest: async (requestId, status, responseMessage) => {
    try {
      const response = await fetch(
        `${API_BASE}/collaborate/${requestId}/respond`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            status,
            responseMessage,
          }),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/requests/pending - Get pending requests
  getPendingRequests: async (page = 1, limit = 10) => {
    try {
      const response = await fetch(
        `${API_BASE}/requests/pending?page=${page}&limit=${limit}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      if (data.success) {
        // Transform the data to match frontend expectations
        const transformedRequests = data.data.requests.map((request) => ({
          ...request,
          id: request._id || request.id, // Ensure id field exists
          requester: request.requester
            ? {
                ...request.requester,
                id: request.requester._id || request.requester.id,
              }
            : null,
          recipient: request.recipient
            ? {
                ...request.recipient,
                id: request.recipient._id || request.recipient.id,
              }
            : null,
          researchRoom: request.researchRoom
            ? {
                ...request.researchRoom,
                id: request.researchRoom._id || request.researchRoom.id,
              }
            : null,
        }));
        return {
          requests: transformedRequests,
          pagination: data.data.pagination,
        };
      }
      return { requests: [], pagination: {} };
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/requests/history - Get request history
  getRequestHistory: async (type = "all", page = 1, limit = 10) => {
    try {
      const response = await fetch(
        `${API_BASE}/requests/history?type=${type}&page=${page}&limit=${limit}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      if (data.success) {
        // Transform the data to match frontend expectations
        const transformedRequests = data.data.requests.map((request) => ({
          ...request,
          id: request._id || request.id, // Ensure id field exists
          requester: request.requester
            ? {
                ...request.requester,
                id: request.requester._id || request.requester.id,
              }
            : null,
          recipient: request.recipient
            ? {
                ...request.recipient,
                id: request.recipient._id || request.recipient.id,
              }
            : null,
          researchRoom: request.researchRoom
            ? {
                ...request.researchRoom,
                id: request.researchRoom._id || request.researchRoom.id,
              }
            : null,
        }));
        return {
          requests: transformedRequests,
          pagination: data.data.pagination,
        };
      }
      return { requests: [], pagination: {} };
    } catch (error) {
      throw error;
    }
  },
};
// 4. RATING SYSTEM (8 endpoints)
export const ratingAPI = {
  // POST /api/collaborators/rate/:userId - Rate collaborator
  rateCollaborator: async (userId, ratingData) => {
    try {
      const response = await fetch(`${API_BASE}/rate/${userId}`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          collaborationRequestId: ratingData.collaborationRequestId,
          overallRating: ratingData.overallRating,
          ratings: {
            communication: ratingData.ratings?.communication,
            reliability: ratingData.ratings?.reliability,
            expertise: ratingData.ratings?.expertise,
            collaboration: ratingData.ratings?.collaboration,
          },
          feedback: ratingData.feedback,
          skillsHighlighted: ratingData.skillsHighlighted || [],
          improvementAreas: ratingData.improvementAreas || [],
          wouldCollaborateAgain: ratingData.wouldCollaborateAgain,
          recommendationLevel: ratingData.recommendationLevel,
          isPublic: ratingData.isPublic !== false,
          isAnonymous: ratingData.isAnonymous || false,
        }),
      });
      return await handleResponse(response);
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/ratings/:userId - Get user ratings
  getUserRatings: async (userId, page = 1, limit = 10) => {
    try {
      const response = await fetch(
        `${API_BASE}/ratings/${userId}?page=${page}&limit=${limit}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      if (data.success) {
        return {
          ratings: data.data.ratings,
          pagination: data.data.pagination,
        };
      }
      return { ratings: [], pagination: {} };
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/ratings/:userId/stats - Get rating statistics
  getRatingStats: async (userId) => {
    try {
      const response = await fetch(`${API_BASE}/ratings/${userId}/stats`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      return data.success ? data.data.stats : null;
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/ratings/pending - Get pending ratings
  getPendingRatings: async () => {
    try {
      const response = await fetch(`${API_BASE}/ratings/pending`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      return data.success ? data.data.pendingRatings : [];
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/ratings/given - Get ratings given by user
  getRatingsGiven: async (page = 1, limit = 10) => {
    try {
      const response = await fetch(
        `${API_BASE}/ratings/given?page=${page}&limit=${limit}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      if (data.success) {
        return {
          ratings: data.data.ratings,
          pagination: data.data.pagination,
        };
      }
      return { ratings: [], pagination: {} };
    } catch (error) {
      throw error;
    }
  },
  // PUT /api/collaborators/ratings/:ratingId/visibility - Update rating visibility
  updateRatingVisibility: async (ratingId, isPublic) => {
    try {
      const response = await fetch(
        `${API_BASE}/ratings/${ratingId}/visibility`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            isPublic,
          }),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      throw error;
    }
  },
  // POST /api/collaborators/ratings/:ratingId/respond - Respond to rating
  respondToRating: async (ratingId, responseMessage) => {
    try {
      const response = await fetch(`${API_BASE}/ratings/${ratingId}/respond`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          message: responseMessage,
        }),
      });
      return await handleResponse(response);
    } catch (error) {
      throw error;
    }
  },
  // DELETE /api/collaborators/ratings/:ratingId - Delete rating
  deleteRating: async (ratingId) => {
    try {
      const response = await fetch(`${API_BASE}/ratings/${ratingId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      return await handleResponse(response);
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/ratings/collaboration/:id/status - Get collaboration rating status
  getCollaborationStatus: async (collaborationId) => {
    try {
      const response = await fetch(
        `${API_BASE}/ratings/collaboration/${collaborationId}/status`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      return data.success ? data.data : null;
    } catch (error) {
      throw error;
    }
  },
};
// 5. SKILL ENDORSEMENTS (8 endpoints)
export const endorsementAPI = {
  // POST /api/collaborators/endorse/:userId - Endorse user's skill
  endorseSkill: async (userId, endorsementData) => {
    try {
      const response = await fetch(`${API_BASE}/endorse/${userId}`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          skill: endorsementData.skill,
          endorsementLevel: endorsementData.endorsementLevel,
          context: endorsementData.context || "collaboration",
          message: endorsementData.message,
          collaborationRequestId: endorsementData.collaborationRequestId,
          researchRoomId: endorsementData.researchRoomId,
        }),
      });
      return await handleResponse(response);
    } catch (error) {
      throw error;
    }
  },
  // PUT /api/collaborators/endorse/:endorsementId/respond - Accept/decline endorsement
  respondToEndorsement: async (endorsementId, isAccepted) => {
    try {
      const response = await fetch(
        `${API_BASE}/endorse/${endorsementId}/respond`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            isAccepted,
          }),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/skills/:userId - Get user's skills
  getUserSkills: async (userId) => {
    try {
      const response = await fetch(`${API_BASE}/skills/${userId}`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      return data.success ? data.data.skills : [];
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/endorsements/pending - Get pending endorsements
  getPendingEndorsements: async (page = 1, limit = 10) => {
    try {
      const response = await fetch(
        `${API_BASE}/endorsements/pending?page=${page}&limit=${limit}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      if (data.success) {
        return {
          endorsements: data.data.endorsements,
          pagination: data.data.pagination,
        };
      }
      return { endorsements: [], pagination: {} };
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/skills/suggestions - Get skill suggestions
  getSkillSuggestions: async (field, limit = 10) => {
    try {
      const response = await fetch(
        `${API_BASE}/skills/suggestions?field=${encodeURIComponent(
          field
        )}&limit=${limit}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      return data.success ? data.data.suggestions : [];
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/endorsements/:userId/stats - Get endorsement stats
  getEndorsementStats: async (userId) => {
    try {
      const response = await fetch(`${API_BASE}/endorsements/${userId}/stats`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      return data.success ? data.data.stats : null;
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/skills/search - Search skills
  searchSkills: async (query, limit = 20) => {
    try {
      const response = await fetch(
        `${API_BASE}/skills/search?query=${encodeURIComponent(
          query
        )}&limit=${limit}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      return data.success ? data.data.skills : [];
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/skills/popular - Get popular skills
  getPopularSkills: async (limit = 20, field = null) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("limit", limit);
      if (field) queryParams.append("field", field);
      const response = await fetch(
        `${API_BASE}/skills/popular?${queryParams.toString()}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      return data.success ? data.data.skills : [];
    } catch (error) {
      throw error;
    }
  },
};
// 6. DISCOVERY & RECOMMENDATIONS (NEW ENTERPRISE FEATURES)
export const discoveryAPI = {
  // GET /api/collaborators/network-dashboard - Unified Network Dashboard
  // Returns all 6 sections in a single API call (LinkedIn-style)
  getNetworkDashboard: async (skipCache = false) => {
    try {
      const params = new URLSearchParams();
      if (skipCache) params.append("skipCache", "true");
      const response = await fetch(
        `${API_BASE}/network-dashboard${params.toString() ? `?${params}` : ""}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      if (data.success) {
        return {
          pendingRequests: data.data.pendingRequests || { items: [], totalCount: 0, hasMore: false },
          suggestions: data.data.suggestions || { items: [], totalCount: 0, hasMore: false },
          institutionPeople: data.data.institutionPeople || { items: [], totalCount: 0, hasMore: false, institutionName: "" },
          trending: data.data.trending || { items: [], totalCount: 0, hasMore: false },
          aiRecommendations: data.data.aiRecommendations || { items: [], totalCount: 0, hasMore: false },
          networkStats: data.data.networkStats || {
            followersCount: 0,
            followingCount: 0,
            pendingRequestsCount: 0,
            collaboratorsCount: 0,
            newFollowersThisWeek: 0,
          },
          meta: data.data.meta || {},
        };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/discover - Personalized discovery feed
  getDiscoveryFeed: async (cursor = null, limit = 20) => {
    try {
      const params = new URLSearchParams();
      if (cursor) params.append("cursor", cursor);
      if (limit) params.append("limit", limit.toString());
      const response = await fetch(`${API_BASE}/discover?${params}`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      if (data.success) {
        return {
          users: data.data.users || [],
          pagination: data.data.pagination || {},
          meta: data.meta || {},
        };
      }
      return { users: [], pagination: {}, meta: {} };
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/trending - Get trending researchers
  getTrendingResearchers: async (cursor = null, limit = 20) => {
    try {
      const params = new URLSearchParams();
      if (cursor) params.append("cursor", cursor);
      if (limit) params.append("limit", limit.toString());
      const response = await fetch(`${API_BASE}/trending?${params}`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      if (data.success) {
        return {
          users: data.data.users || [],
          pagination: data.data.pagination || {},
          meta: data.meta || {},
        };
      }
      return { users: [], pagination: {}, meta: {} };
    } catch (error) {
      throw error;
    }
  },
  // GET /api/collaborators/recommendations - ML-powered recommendations
  getMLRecommendations: async (cursor = null, limit = 20) => {
    try {
      const params = new URLSearchParams();
      if (cursor) params.append("cursor", cursor);
      if (limit) params.append("limit", limit.toString());
      const response = await fetch(`${API_BASE}/recommendations?${params}`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      if (data.success) {
        return {
          users: data.data.users || [],
          pagination: data.data.pagination || {},
          meta: data.meta || {},
        };
      }
      return { users: [], pagination: {}, meta: {} };
    } catch (error) {
      throw error;
    }
  },
};
// Enhanced search API with Elasticsearch support
export const enhancedSearchAPI = {
  // Enhanced search with Elasticsearch and highlights
  searchCollaborators: async (searchParams) => {
    try {
      const {
        query = "",
        field = "",
        interests = "",
        isOpenForCollaboration,
        isVerified,
        minRating,
        location = "",
        page = 1,
        limit = 20,
        sortBy = "relevance",
        cursor = null,
      } = searchParams;
      const params = new URLSearchParams();
      if (query) params.append("query", query);
      if (field) params.append("field", field);
      if (interests) params.append("interests", interests);
      if (location) params.append("location", location);
      // Only send boolean filters when explicitly TRUE
      // This ensures backend returns ALL results unless user enables filter
      if (isOpenForCollaboration === true)
        params.append("isOpenForCollaboration", "true");
      if (isVerified === true)
        params.append("isVerified", "true");
      if (minRating) params.append("minRating", minRating.toString());
      if (cursor) params.append("cursor", cursor);
      else params.append("page", page.toString());
      params.append("limit", limit.toString());
      params.append("sortBy", sortBy);
      const response = await fetch(`${API_BASE}/search?${params}`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      if (data.success) {
        return {
          users: data.data.users || [],
          pagination: data.data.pagination || {},
          meta: data.meta || {},
        };
      }
      return { users: [], pagination: {}, meta: {} };
    } catch (error) {
      throw error;
    }
  },
};
// Enhanced following API with cursor pagination support
export const enhancedFollowingAPI = {
  ...followingAPI,
  // Enhanced getFollowers with cursor support
  getFollowersWithCursor: async (userId, cursor = null, limit = 20) => {
    try {
      const params = new URLSearchParams();
      if (cursor) params.append("cursor", cursor);
      else params.append("page", 1);
      params.append("limit", limit.toString());
      const response = await fetch(
        `${API_BASE}/followers/${userId}?${params}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      if (data.success) {
        return {
          followers: data.data.followers || [],
          pagination: data.data.pagination || {},
          meta: data.meta || {},
        };
      }
      return { followers: [], pagination: {}, meta: {} };
    } catch (error) {
      throw error;
    }
  },
  // Enhanced getFollowing with cursor support
  getFollowingWithCursor: async (userId, cursor = null, limit = 20) => {
    try {
      const params = new URLSearchParams();
      if (cursor) params.append("cursor", cursor);
      else params.append("page", 1);
      params.append("limit", limit.toString());
      const response = await fetch(
        `${API_BASE}/following/${userId}?${params}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      if (data.success) {
        return {
          following: data.data.following || [],
          pagination: data.data.pagination || {},
          meta: data.meta || {},
        };
      }
      return { following: [], pagination: {}, meta: {} };
    } catch (error) {
      throw error;
    }
  },
  // Enhanced follow suggestions with ML scoring
  getEnhancedFollowSuggestions: async (limit = 10) => {
    try {
      const response = await fetch(`${API_BASE}/suggestions?limit=${limit}`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      if (data.success) {
        return data.data.suggestions || [];
      }
      return [];
    } catch (error) {
      throw error;
    }
  },
};
// Combined enhanced export
export const collaboratorsAPI = {
  profile: profileAPI,
  following: enhancedFollowingAPI,
  collaboration: collaborationAPI,
  rating: ratingAPI,
  endorsement: endorsementAPI,
  search: enhancedSearchAPI,
  discovery: discoveryAPI, // NEW
  dashboard: dashboardAPI, // NEW - Unified Dashboard API
};
export default collaboratorsAPI;
